class PersonalisationFileUpload extends window.BAO.CustomElement() {
  constructor () {
    super()

    this.isDragging = false

    this.input = this.els.input?.element
    this.label = this.els.label?.element
    this.button = this.els.button?.element
    this.preview = this.els.preview?.element
    this.previewImage = this.els.image?.element
    this.fileName = this.els['file-name']?.element
    this.fileSize = this.els['file-size']?.element
    this.removeButton = this.els.remove?.element
    this.footer = this.els.footer?.element
    this.error = this.els.error?.element
    this.cropImage = this.els.cropper?.element
    this.cropContainer = this.els.cropContainer?.element
    this.cropConfirm = this.els.cropConfirm?.element
    this.cropPreview = this.els.cropPreview?.element
  }

  connectedCallback () {
    super.connectedCallback()
  }

  setupListeners () {
    if (this.input) {
      this.listeners.add(
        this.input,
        'change',
        this.handleFileSelect.bind(this),
      )

      this.listeners.add(
        this.input,
        'dragenter',
        this.handleDragOver.bind(this),
      )

      this.listeners.add(
        this.input,
        'dragleave',
        this.handleDragLeave.bind(this),
      )

      this.listeners.add(this.input, 'focus', this.clearError.bind(this))
      this.listeners.add(this.input, 'click', this.clearError.bind(this))

      window.BAO.eventBus.addEventListener(
        window.BAO.EVENTS.PDP.CLEAN_UP,
        this.handleRemoveFile.bind(this),
      )
    }

    this.els.uploadChoice?.elements.forEach((el) => {
      this.listeners.add(el, 'change', this.handleUploadChoice.bind(this))
    })

    if (this.button) {
      this.listeners.add(
        this.button,
        'click',
        this.handleButtonClick.bind(this),
      )
    }

    if (this.removeButton) {
      this.listeners.add(
        this.removeButton,
        'click',
        this.handleRemoveFile.bind(this),
      )
    }

    if (this.cropConfirm) {
      this.listeners.add(
        this.cropConfirm,
        'click',
        this.confirmCrop.bind(this),
      )
    }
  }

  handleUploadChoice (e) {
    const { currentTarget } = e

    const isUserUpload =
      currentTarget.getAttribute('aria-controls') === 'userUpload'

    this.els.uploadContainer?.element.setAttribute(
      'aria-hidden',
      !isUserUpload,
    )

    if (this.input) {
      this.input.disabled = !isUserUpload
    }

    if (this.cropPreview) {
      this.cropPreview.value = isUserUpload
        ? this.cropPreview.dataset?.value || ''
        : this.cropPreview.dataset.default

      this.cropPreview.dispatchEvent(new Event('change'))
    }
  }

  handleFileSelect (event) {
    const file = event.target.files[0]
    const uploadError = this.querySelector(
      '[data-personalisation-form-el="uploadError"]',
    )
    this.error.setAttribute('aria-hidden', 'true')

    if (file && file.type.includes('image')) {
      const maxSizeInBytes = 19 * 1024 * 1024

      if (file.size > maxSizeInBytes) {
        this.error.setAttribute('aria-hidden', 'false')
        this.input.value = ''
        return
      }

      this.showCropper(file)

      if (uploadError) {
        uploadError.setAttribute('aria-hidden', 'true')
      }
    } else {
      uploadError?.setAttribute('aria-hidden', 'false')
    }
  }

  showCropper (file) {
    if (!this.cropImage) return

    this.cropContainer.setAttribute('aria-hidden', 'false')

    const imageUrl = URL.createObjectURL(file)
    this.cropImage.src = imageUrl

    if (this.input) {
      this.input.setAttribute('aria-hidden', 'true')
    }
    if (this.label) {
      this.label.setAttribute('aria-hidden', 'true')
    }

    this.cropper = new window.Cropper(this.cropImage, {
      initialAspectRatio: parseFloat(this.cropImage.dataset.aspectRatio),
      autoCropArea: 1,
      minContainerHeight: this.clientWidth,
      crop: (event) => {
        window.clearTimeout(this.timer)
        this.timer = window.setTimeout(() => {
          if (this.cropPreview) {
            const croppedImage = this.cropper
              .getCroppedCanvas({
                fillColor: '#fff',
              })
              .toDataURL('image/jpeg')

            this.previewImage.src = croppedImage
            this.cropPreview.value = croppedImage
            this.cropPreview.dataset.value = croppedImage
            this.cropPreview.dispatchEvent(new Event('change'))

            this.currentImageUrl = croppedImage
          }
        }, 250)
      },
    })

    this.showPreview(file)
  }

  confirmCrop () {
    this.cropContainer.setAttribute('aria-hidden', 'true')
    this.cropper.destroy()
  }

  showPreview (file) {
    if (this.fileName) {
      this.fileName.textContent = file.name
    }

    if (this.fileSize) {
      const sizeInKB = Math.round(file.size / 1024)
      this.fileSize.textContent = `${sizeInKB} KB`
    }

    if (this.preview) {
      this.preview.setAttribute('aria-hidden', 'false')
    }
    if (this.input) {
      this.input.setAttribute('aria-hidden', 'true')
    }
    if (this.label) {
      this.label.setAttribute('aria-hidden', 'true')
    }
    if (this.footer) {
      this.footer.setAttribute('aria-hidden', 'true')
    }
  }

  handleRemoveFile () {
    this.input.value = ''

    this.input.dispatchEvent(new Event('change', { bubbles: true }))

    if (this.currentImageUrl) {
      URL.revokeObjectURL(this.currentImageUrl)
      this.currentImageUrl = null
    }

    this.cropContainer?.setAttribute('aria-hidden', 'true')
    this.preview?.setAttribute('aria-hidden', 'true')
    this.input?.setAttribute('aria-hidden', 'false')
    this.label?.setAttribute('aria-hidden', 'false')
    this.footer?.setAttribute('aria-hidden', 'false')

    if (this.cropPreview) {
      this.cropPreview.value = ''
      this.cropPreview.dataset.value = ''
      this.cropPreview.dispatchEvent(new Event('change'))
    }
    if (this.cropper) {
      this.cropper.destroy()
    }
    if (this.cropImage) {
      this.cropImage.src = ''
    }
    if (this.previewImage) {
      this.previewImage.src = ''
    }
    if (this.fileName) {
      this.fileName.textContent = ''
    }
    if (this.fileSize) {
      this.fileSize.textContent = ''
    }
  }

  handleDragOver () {
    this.label.classList.add('prd-Personalisation_UploadForm-dragging')
  }

  handleDragLeave () {
    this.label.classList.remove('prd-Personalisation_UploadForm-dragging')
  }

  handleButtonClick () {
    this.clearError()

    this.input.click()
  }

  clearError () {
    if (this.error) {
      this.error.setAttribute('aria-hidden', 'true')
    }
  }
}

if (!customElements.get('personalisation-file-upload')) {
  customElements.define(
    'personalisation-file-upload',
    PersonalisationFileUpload,
  )
}
